// The crawl you're on, kept in this browser until you end it: a reload, a
// closed tab or a dead phone battery shouldn't lose it. Only what's needed to
// rebuild it is stored (the pubs, in order, and the times); scores are fetched
// fresh. Storage may be unavailable (private mode), so every access is guarded.

import { DWELL_STEPS, MAX_STOPS, MIN_STOPS } from './config';
import type { CrawlMode, CrawlStart } from './plan';
import type { CrawlForm } from './useCrawl';

export interface SavedCrawl {
  /** The pubs' OSM ids, in visiting order. */
  ids: string[];
  /** Setting off from the start (or arriving at the first pub, with no start). */
  startTime: number;
  dwell: number;
  start: CrawlStart | null;
  mode: CrawlMode | null;
  /** Opened from someone else's link. */
  shared: boolean;
  /** The planner's settings, so "Change this crawl" picks up where you were. */
  form: CrawlForm;
}

const KEY = 'bgi:crawl';
const VERSION = 1;
const PUB_ID = /^[nwr]\d{1,12}$/;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function readStart(v: unknown): CrawlStart | null {
  if (!v || typeof v !== 'object') return null;
  const s = v as Partial<CrawlStart>;
  if (!isNum(s.lat) || !isNum(s.lon) || Math.abs(s.lat) > 90 || Math.abs(s.lon) > 180) return null;
  return { lat: s.lat, lon: s.lon, label: typeof s.label === 'string' ? s.label.slice(0, 80) : '' };
}

const readMode = (v: unknown): CrawlMode | null => (v === 'best' || v === 'closest' ? v : null);

function readForm(v: unknown, fallback: CrawlForm): CrawlForm {
  if (!v || typeof v !== 'object') return fallback;
  const f = v as Partial<CrawlForm>;
  const filters = (f.filters ?? {}) as Partial<CrawlForm['filters']>;
  return {
    start: readStart(f.start),
    stops: isNum(f.stops)
      ? Math.min(MAX_STOPS, Math.max(MIN_STOPS, Math.round(f.stops)))
      : fallback.stops,
    maxLeg: isNum(f.maxLeg) && f.maxLeg > 0 ? f.maxLeg : fallback.maxLeg,
    maxTotal: f.maxTotal === null ? null : isNum(f.maxTotal) ? f.maxTotal : fallback.maxTotal,
    startTime: isNum(f.startTime) ? f.startTime : fallback.startTime,
    dwell: (DWELL_STEPS as readonly number[]).includes(f.dwell as number)
      ? (f.dwell as number)
      : fallback.dwell,
    mode: readMode(f.mode) ?? fallback.mode,
    filters: {
      confirmed: filters.confirmed === true,
      dog: filters.dog === true,
      ale: filters.ale === true,
      food: filters.food === true,
    },
  };
}

/** The saved crawl, or null if there isn't a usable one. */
export function loadSavedCrawl(fallbackForm: CrawlForm): SavedCrawl | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (v.v !== VERSION || !Array.isArray(v.ids)) return null;
    const ids = v.ids
      .filter((id): id is string => typeof id === 'string' && PUB_ID.test(id))
      .slice(0, MAX_STOPS);
    if (ids.length === 0 || !isNum(v.startTime)) return null;
    return {
      ids,
      startTime: v.startTime,
      dwell: (DWELL_STEPS as readonly number[]).includes(v.dwell as number)
        ? (v.dwell as number)
        : 45,
      start: readStart(v.start),
      mode: readMode(v.mode),
      shared: v.shared === true,
      form: readForm(v.form, fallbackForm),
    };
  } catch {
    return null;
  }
}

export function saveCrawl(saved: SavedCrawl): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: VERSION, ...saved }));
  } catch {
    // ignore
  }
}

export function clearSavedCrawl(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/** Whether there's a crawl to come back to (cheap: for the first render). */
export function hasSavedCrawl(): boolean {
  try {
    return localStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}
